import { BfGroupName, BfTokenClaims } from "./api/models";
import jwtDecode from "jwt-decode";

const PAYLOAD_LEN = 43;
const SIGNATURE_LEN = 64;
const FORMAT_VERSION = 1;
const ISSUER = "bushfire.io";

/// Bit positions mirror the API's compact token format: never reorder them.
const GROUP_BITS: BfGroupName[] = [
    BfGroupName.user,
    BfGroupName.pro,
    BfGroupName.root,
    BfGroupName.developer,
    BfGroupName.licencing,
    BfGroupName.history,
    BfGroupName.management,
    BfGroupName.email,
    BfGroupName.business,
    BfGroupName.webhook,
    BfGroupName.cloud,
    BfGroupName.enterprise,
    BfGroupName.deprecated,
    BfGroupName.moderator,
];

/** True for the API's compact tokens (`bfs_`, `bft_`, `bfk_`), false for JWTs. */
export const isCompactToken = (token: string): boolean =>
    token.startsWith("bf") && token.charAt(3) === "_";

const base64UrlBytes = (value: string): Uint8Array | null => {
    try {
        const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
        return Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    } catch {
        return null;
    }
};

const hex = (bytes: Uint8Array): string =>
    Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

const uuid = (bytes: Uint8Array): string => {
    const digits = hex(bytes);
    return [
        digits.slice(0, 8),
        digits.slice(8, 12),
        digits.slice(12, 16),
        digits.slice(16, 20),
        digits.slice(20),
    ].join("-");
};

/** Reads a compact token's claims; the signature is not checked, the API and tiley do that. */
const decodeCompact = (token: string): BfTokenClaims | null => {
    const kindEnd = token.indexOf("_");
    const environmentEnd = token.indexOf("_", kindEnd + 1);
    if (kindEnd < 0 || environmentEnd < 0) return null;

    const bytes = base64UrlBytes(token.slice(environmentEnd + 1));
    if (!bytes || bytes.length !== PAYLOAD_LEN + SIGNATURE_LEN) return null;
    if (bytes[0] !== FORMAT_VERSION) return null;

    const view = new DataView(bytes.buffer, bytes.byteOffset, PAYLOAD_LEN);
    const issuedAt = view.getUint32(27);
    const groupBits = view.getUint32(37);

    return {
        iss: ISSUER,
        sub: uuid(bytes.slice(3, 19)),
        jti: hex(bytes.slice(19, 27)),
        nbf: issuedAt,
        exp: view.getUint32(31),
        gxp: issuedAt + view.getUint16(35) * 3600,
        group: GROUP_BITS.filter((_, bit) => (groupBits & (1 << bit)) !== 0),
    };
};

/** A session token's claims, JWT or compact; null when it cannot be read. */
export const decodeToken = (token?: string | null): BfTokenClaims | null => {
    if (!token) return null;

    if (isCompactToken(token)) return decodeCompact(token);

    try {
        return jwtDecode<BfTokenClaims>(token);
    } catch {
        return null;
    }
};

/**
 * The credential map requests carry to tiley: the tile token when the session has one, else a
 * legacy JWT session token. A compact session token is never sent, tiley refuses it.
 */
export const tileRequestToken = (
    sessionToken?: string | null,
    tileToken?: string | null
): string | undefined => {
    if (tileToken) return tileToken;
    if (sessionToken && !isCompactToken(sessionToken)) return sessionToken;
    return undefined;
};
